import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpDown, Check, ChevronDown, Search, X } from 'lucide-react';
import {
  FarmIssueRule,
  FarmIssueSeverity,
  FarmsOverviewQuery,
  FarmsOverviewSort,
  FarmStatus,
  FarmWeighing
} from '../../models/farms-overview';

interface FilterSelectOption<T> {
  value: T;
  label: string;
}

interface FilterSelectProps<T> {
  label: string;
  value: T | null;
  /** Label for the null option: "all rules" for a filter, "worst first" for the order. */
  emptyLabel: string;
  options: FilterSelectOption<T>[];
  onChange: (value: T | null) => void;
  /** Drawn in the closed button, ahead of the value. Marks a control that is not a filter. */
  icon?: React.ReactNode;
}

/**
 * The same hand-rolled listbox the dashboard's period select uses, generalised over the option type.
 * Kept out of `elements/` for now: those are all MUI v4 controls, and dropping a Tailwind one in beside
 * them would blur where the boundary between the two styling systems runs.
 *
 * Every native button style is reset explicitly - this project loads Tailwind's theme and utilities
 * without preflight, so a bare <button> keeps the browser's grey face and border.
 */
function FilterSelect<T extends string>({ label, value, emptyLabel, options, onChange, icon }: FilterSelectProps<T>) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const entries: FilterSelectOption<T | null>[] = [{ value: null, label: emptyLabel }, ...options];
  const selected = entries.find((entry) => entry.value === value) ?? entries[0];

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((isOpen) => !isOpen)}
        className="flex h-9 w-48 cursor-pointer appearance-none items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-900 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
      >
        {icon}
        <span className="mr-auto truncate">{selected.label}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={label}
          className="absolute left-0 z-50 mt-1 w-48 overflow-hidden rounded-md border border-gray-200 bg-white p-1 shadow-md"
        >
          {entries.map((entry) => {
            const isSelected = entry.value === value;

            return (
              <button
                key={entry.value ?? 'all'}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onChange(entry.value);
                  setOpen(false);
                }}
                className={`flex w-full cursor-pointer appearance-none items-center gap-2 rounded-sm border-0 bg-transparent py-1.5 pr-2 pl-2 text-left text-sm transition-colors hover:bg-gray-100 ${
                  isSelected ? 'text-gray-900' : 'text-gray-700'
                }`}
              >
                <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                  {isSelected && <Check className="h-4 w-4 text-indigo-600" aria-hidden="true" />}
                </span>
                <span className="truncate">{entry.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export interface FarmsOverviewFiltersProps {
  value: FarmsOverviewQuery;
  onChange: (value: FarmsOverviewQuery) => void;
}

export default function FarmsOverviewFilters({ value, onChange }: FarmsOverviewFiltersProps) {
  const { t } = useTranslation();

  // Any change here sends the reader back to page 1. Narrowing, because staying on page 4 of a list that
  // just shrank to one page is an empty screen with no explanation on it; re-ordering, because page 4 of a
  // list sorted differently holds different farms, and "worst first" only means anything from the top.
  const update = (patch: Partial<FarmsOverviewQuery>) => onChange({ ...value, ...patch, page: 0 });

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Search, status, rule and severity are all applied by the API - it matches the UBN or the farm
          name, and the list is paged, so filtering the returned page here would only narrow the rows that
          happened to land on it. */}
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type="search"
          value={value.searchText}
          onChange={(event) => update({ searchText: event.target.value })}
          placeholder={t('f-com-search-farm')}
          aria-label={t('f-com-search-farm')}
          // The native webkit clear button is suppressed: this input already has one, and Chrome would
          // otherwise draw a second, differently-styled cross beside it.
          className="h-9 w-64 appearance-none rounded-md border border-gray-200 bg-white pr-8 pl-9 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 [&::-webkit-search-cancel-button]:appearance-none"
        />
        {value.searchText && (
          <button
            type="button"
            onClick={() => update({ searchText: '' })}
            aria-label={t('f-com-clear-filters')}
            className="absolute top-1/2 right-2 flex h-5 w-5 -translate-y-1/2 cursor-pointer appearance-none items-center justify-center rounded-sm border-0 bg-transparent text-gray-400 transition-colors hover:text-gray-700"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* First of the three, and the one most readers will touch: the list now holds every farm, so
          "only the ones that need a call" is the narrowing that used to be the whole screen. */}
      <FilterSelect
        label={t('f-farms-overview-filter-status')}
        value={value.status}
        emptyLabel={t('f-farms-overview-filter-status-all')}
        onChange={(status) => update({ status })}
        options={[
          { value: FarmStatus.Critical, label: t('f-farms-overview-severity-critical') },
          { value: FarmStatus.Warning, label: t('f-farms-overview-severity-warning') },
          { value: FarmStatus.OnTrack, label: t('f-farms-overview-status-on-track') },
          { value: FarmStatus.NotMonitored, label: t('f-farms-overview-status-not-monitored') }
        ]}
      />

      {/* Its own control rather than more entries in the status select: whether a farm registers what it
          should and whether it weighs what it registers are two questions, and the pairing worth finding -
          a farm on track that weighs nothing - can only be asked for if they can be combined. */}
      <FilterSelect
        label={t('f-farms-overview-filter-weighing')}
        value={value.weighing}
        emptyLabel={t('f-farms-overview-filter-weighing-all')}
        onChange={(weighing) => update({ weighing })}
        options={[
          { value: FarmWeighing.OnTrack, label: t('f-farms-overview-weighing-on-track') },
          { value: FarmWeighing.Behind, label: t('f-farms-overview-weighing-behind') },
          { value: FarmWeighing.NoScale, label: t('f-farms-overview-weighing-no-scale') }
        ]}
      />

      <FilterSelect
        label={t('f-farms-overview-filter-rule')}
        value={value.rule}
        emptyLabel={t('f-farms-overview-filter-rule-all')}
        onChange={(rule) => update({ rule })}
        options={[
          { value: FarmIssueRule.UnderRegistered, label: t('f-farms-overview-rule-under-registered') },
          { value: FarmIssueRule.NoRegistrations, label: t('f-farms-overview-rule-no-registrations') },
          { value: FarmIssueRule.OverRegistered, label: t('f-farms-overview-rule-over-registered') }
        ]}
      />

      <FilterSelect
        label={t('f-farms-overview-filter-severity')}
        value={value.severity}
        emptyLabel={t('f-farms-overview-filter-severity-all')}
        onChange={(severity) => update({ severity })}
        options={[
          { value: FarmIssueSeverity.Critical, label: t('f-farms-overview-severity-critical') },
          { value: FarmIssueSeverity.Warning, label: t('f-farms-overview-severity-warning') }
        ]}
      />

      {/* Pushed to the far end and carrying an icon, because it is the one control here that does not change
          which farms are listed - only the order they arrive in. Its closed label is also the only place the
          current order is stated, which is why the queue heading claims no order of its own. */}
      <div className="ml-auto">
        <FilterSelect
          label={t('f-farms-overview-sort')}
          value={value.sort}
          emptyLabel={t('f-farms-overview-sort-severity')}
          onChange={(sort) => update({ sort })}
          icon={<ArrowUpDown className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />}
          options={[
            { value: FarmsOverviewSort.MostRegistered, label: t('f-farms-overview-sort-registered-desc') },
            { value: FarmsOverviewSort.FewestRegistered, label: t('f-farms-overview-sort-registered') },
            { value: FarmsOverviewSort.MostWeighed, label: t('f-farms-overview-sort-weighed-desc') },
            { value: FarmsOverviewSort.LeastWeighed, label: t('f-farms-overview-sort-weighed') },
            { value: FarmsOverviewSort.LongestSilent, label: t('f-farms-overview-sort-silent-desc') },
            { value: FarmsOverviewSort.ShortestSilent, label: t('f-farms-overview-sort-silent') },
            { value: FarmsOverviewSort.FarmName, label: t('f-farms-overview-sort-farm-name') },
            { value: FarmsOverviewSort.FarmNumber, label: t('f-farms-overview-sort-farm-number') }
          ]}
        />
      </div>
    </div>
  );
}
